import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { API } from './config';
import { useAuth } from './App';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const { login } = useAuth();

  useEffect(() => {
    // Prevent duplicate processing (StrictMode protection)
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error('Authentication failed - no session ID');
          navigate('/', { replace: true });
          return;
        }

        // Phase R1 (2026-06-25): recover referral code that survived the
        // OAuth round-trip. Prefer the URL search param (since AuthModal
        // encoded it into the redirect target); fall back to localStorage.
        // Both are best-effort — invalid/missing codes silently no-op.
        let referralCode = null;
        try {
          const sp = new URLSearchParams(window.location.search);
          const fromUrl = (sp.get('ref') || '').trim();
          if (fromUrl && fromUrl.length <= 64 && /^[A-Za-z0-9_-]+$/.test(fromUrl)) {
            referralCode = fromUrl;
          }
        } catch (e) { /* noop */ }
        if (!referralCode) {
          try {
            const raw = localStorage.getItem('onlymass_referral_code');
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed && parsed.code && parsed.expires_at && Date.now() < parsed.expires_at) {
                referralCode = parsed.code;
              }
            }
          } catch (e) { /* noop */ }
        }

        // Exchange session_id for user data and session_token
        const response = await axios.post(
          `${API}/auth/session-data`,
          referralCode ? { referral_code: referralCode } : {},
          {
            headers: {
              'X-Session-ID': sessionId,
              'Content-Type': 'application/json'
            }
          }
        );

        const { user, session_token } = response.data;

        if (!user || !session_token) {
          throw new Error('Invalid response from server');
        }

        // Update React auth state AND set cookies via AuthProvider.login()
        // (writing cookies alone is not enough — React state stays null and
        //  ProtectedRoute bounces back to "/" before AuthProvider re-reads cookies)
        login(user, session_token);

        // Phase R1: clear consumed referral code from localStorage on success.
        try { localStorage.removeItem('onlymass_referral_code'); } catch (e) { /* noop */ }

        toast.success(`Welcome${user.name ? `, ${user.name}` : ''}!`);

        // Clean the hash and ref query from the URL and navigate
        navigate('/dashboard', { replace: true });
      } catch (error) {
        const errorMessage = error.response?.data?.detail || 'Authentication failed';
        toast.error(errorMessage);
        navigate('/', { replace: true });
      }
    };

    processSession();
  }, [location.hash, navigate, login]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
